import { Button, FormControl, Grid, Group, IconButton, Layout, Page } from "@/comn/components";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { useFetch, useForm, useGrid, useModal, usePage, useResource, useStore, useToast, useAuth } from "@/comn/hooks";
import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    APIS,
    URLS,
    SF_DOC_TRMSSN_MGMT_SRCH,
    SG_DOC_TRMSSN_MGMT_LIST,
} from "@/tra/tancis/ptli/elctdoc/trmssn/services/PtliDocTrmssnMgmtService";
import { values } from "lodash";

/*
 * Electronic Document transmission Management
 * !== 전자문서 발신 관리 ==!
 */
export const PTLI0702001Q = () => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0047", "auth_ptli0004", "auth_ptli0049", "auth_ptli0015", "auth_ptlianyADM", "auth_ptli0048"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0081" },
            { area: "comnCd", comnCd: "COM0070" },
            { area: "comnCd", comnCd: "COM0084" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        docTrmssnMgmtSrch: useForm({
            defaultSchema: SF_DOC_TRMSSN_MGMT_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {}),
                startFrstRgsrDtm: pgeStore?.form?.startFrstRgsrDtm || comnUtils.getDate(),
                endFrstRgsrDtm: pgeStore?.form?.endFrstRgsrDtm || comnUtils.getDate(),
            },
        }),
    };

    const grid = {
        docTrmssnMgmtList: useGrid({
            defaultSchema: SG_DOC_TRMSSN_MGMT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get a list of transmission electronic documents. !== 전자문서 발신 목록을 조회한다. ==!
        getElctDocTrmssnList: useFetch({
            api: (page = grid.docTrmssnMgmtList.page) =>
                APIS.getElctDocTrmssnList(form.docTrmssnMgmtSrch.getValues(), page, grid.docTrmssnMgmtList.size),
            enabled: comnUtils.isEmpty(form.docTrmssnMgmtSrch.errors),
            key: [grid.docTrmssnMgmtList.page, grid.docTrmssnMgmtList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.docTrmssnMgmtSrch.getValues(),
                    page: grid.docTrmssnMgmtList.page,
                    size: grid.docTrmssnMgmtList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        getElctDocTrmssnList: (page: number = 0) => {
            form.docTrmssnMgmtSrch.handleSubmit(
                () => {
                    grid.docTrmssnMgmtList.setPage(page);
                    fetch.getElctDocTrmssnList.fetch(page);
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                },
            )();
        },
        // Enter Keyword text !== 검색어 엔터 이벤트 ==!
        onEnterKwdTxt: () => {
            grid.docTrmssnMgmtList.setPage(0);
            fetch.getElctDocTrmssnList.fetch(0);
        },
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        grid_docTrmssnMgmtList: {
            cell: {
                docNtcUuid: (props: any) => {
                    const { rowValues, value } = props;
                    return <Link to={`/ptli/elctdoc/trmssn/ptli0702002s/${rowValues.docNtcUuid}`}>{value}</Link>;
                },
            },
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {}, []);

    const kwdSel = form.docTrmssnMgmtSrch.watch("kwdSel");

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.docTrmssnMgmtSrch.schema.bsopClsfCd}></Group.Control>
                            <Group.Control {...form.docTrmssnMgmtSrch.schema.startTimeRnge}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Label label={"L_KWD"} />
                            <Group.Col padding={1}>
                                <Layout direction="row" gap={1}>
                                    <FormControl {...form.docTrmssnMgmtSrch.schema.kwdSel} />
                                    <FormControl
                                        {...form.docTrmssnMgmtSrch.schema.kwdTxt}
                                        readOnly={!kwdSel}
                                        onEnter={handler.onEnterKwdTxt}
                                    />
                                </Layout>
                            </Group.Col>
                            <Group.Control {...form.docTrmssnMgmtSrch.schema.elctPrcssStatCd}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.docTrmssnMgmtSrch.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role="search"
                                onClick={() => {
                                    handler.getElctDocTrmssnList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.docTrmssnMgmtList.grid}
                            data={fetch.getElctDocTrmssnList.data?.elctDocTrmssnList}
                            render={render.grid_docTrmssnMgmtList}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
        </Page>
    );
};
